import type { DriverAction } from '@automata/wfb-api';
import { ParameterDefinition } from '@automata/wfb-api/src/apis/wfbApiSchemas';

import type {
  TaskParameterValue,
  TaskProperties,
  TimeEstimateType,
} from '../types';

type TaskParameters = Record<string, TaskParameterValue>;

// TODO(IL, 2024-05-17): we might need to add more names of "parameters which are used as time estimates" later,
// depending on how uniformly-written the drivers are. But ultimately, the API should be supplying "durationBased" and "driverSpecified" time estimates
// to the scheduler, rather than WFB GUI forwarding driver data to the API (see https://automata-tech.atlassian.net/browse/LA-346 as a starting point / prereq.)
//
// If the API can fill in time estimates for "durationBased" and "driverSpecified", then WFB GUI will need to tell from driver data or another API endpoint
// whether a given action is "userEstimated" or not so that WFB GUI can conditionally render the time estimate field and
// can determine if that user-specified value is required).
// But WFB GUI would no longer have to care about whether a driver is "durationBased" vs "driverSpecified" bc the API would deal with that opaquely.
const HARDCODED_DURATION_PARAMETER_NAME = 'duration';

function _isDurationBased(action: DriverAction): boolean {
  return action.arguments?.[HARDCODED_DURATION_PARAMETER_NAME] !== undefined;
}

/** For a task with a duration-based time estimate, get the user-specified duration value */
export function getDurationValue({
  action,
  parameterDefinitions,
  parameters,
}: {
  action: DriverAction;
  parameterDefinitions: ParameterDefinition[];
  parameters: TaskParameters;
}): number | null {
  if (_isDurationBased(action)) {
    // Here we get a value or parameter_id if value does not exist in parameters
    const duration = parameters[HARDCODED_DURATION_PARAMETER_NAME];
    if (typeof duration === 'number') {
      return duration;
    }
    if (typeof duration === 'string') {
      const defaultValue = parameterDefinitions.find(
        (p) => p.id === duration
      )?.default;
      return typeof defaultValue === 'number' ? defaultValue : null;
    }
  }

  return null;
}

export const _getValue = (value: number | null) => {
  if (value === null) return null;
  return Math.round(value); // value must be int rather than float for validation
};

export const getTaskTimeEstimateTypeAndValue = ({
  action,
  parameters,
  userTimeEstimate,
  parameterDefinitions,
}: {
  action: DriverAction;
  parameters: TaskParameters;
  userTimeEstimate: TaskProperties['userTimeEstimate'];
  parameterDefinitions: ParameterDefinition[];
}): {
  timeEstimateType: TimeEstimateType;
  value: number | null;
} => {
  const driverSpecifiedTimeEstimate = action.time_estimate;

  if (driverSpecifiedTimeEstimate !== null) {
    return {
      timeEstimateType: 'driverSpecified',
      value: _getValue(driverSpecifiedTimeEstimate),
    };
  }

  if (_isDurationBased(action)) {
    const durationValue = getDurationValue({
      action,
      parameters,
      parameterDefinitions,
    });

    return {
      timeEstimateType: 'durationBased',
      value: _getValue(durationValue),
    };
  }

  return {
    timeEstimateType: 'userEstimated',
    value: _getValue(userTimeEstimate),
  };
};
